import { Target, Star, ExternalLink, ShoppingBag, Megaphone } from 'lucide-react'

export default function CompetitorsTable({ competitors }) {
  if (!competitors || competitors.length === 0) return null

  const amazonCount  = competitors.filter(c => c.source === 'amazon').length
  const facebookCount = competitors.filter(c => c.source === 'facebook').length

  return (
    <div className="gradient-glow p-6 rounded-xl card-hover">
      <div className="flex items-center gap-3 mb-6">
        <Target className="w-6 h-6 text-slate-medium" />
        <div>
          <h3 className="text-xl font-bold text-white">Principales Competidores</h3>
          <p className="text-xs text-slate-light mt-0.5">
            {amazonCount > 0 && `${amazonCount} en Amazon`}
            {amazonCount > 0 && facebookCount > 0 && ' · '}
            {facebookCount > 0 && `${facebookCount} anunciantes en Facebook Ads`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {competitors.map((competitor, idx) => {
          const isFb = competitor.source === 'facebook'
          return (
            <div
              key={idx}
              className="p-4 bg-slate-darker border border-slate-medium/30 rounded-lg hover:border-slate-medium/60 hover:shadow-lg hover:shadow-slate-medium/20 transition-all flex flex-col gap-3"
            >
              {/* Header: nombre + fuente */}
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-white leading-snug line-clamp-3 flex-1">
                  {competitor.name}
                </p>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-xs px-2 py-0.5 bg-slate-medium/20 text-slate-medium rounded">
                    #{idx + 1}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${
                    isFb
                      ? 'bg-blue-900/30 border border-blue-700/30 text-blue-300'
                      : 'bg-orange-900/20 border border-orange-700/20 text-orange-300'
                  }`}>
                    {isFb
                      ? <><Megaphone className="w-2.5 h-2.5" /> FB Ads</>
                      : <><ShoppingBag className="w-2.5 h-2.5" /> Amazon</>
                    }
                  </span>
                </div>
              </div>

              {/* Posicionamiento */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-slate-400">{isFb ? 'Actividad publicitaria' : 'Posicionamiento'}</p>
                  <span className="text-xs font-bold text-slate-300">{competitor.strength}%</span>
                </div>
                <div className="h-1.5 bg-slate-medium/20 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${competitor.strength}%`,
                      background: isFb
                        ? 'linear-gradient(90deg, #2563eb, #60a5fa)'
                        : 'linear-gradient(90deg, #6b7280, #d1d5db)',
                    }}
                  />
                </div>
              </div>

              {/* Precio (Amazon) */}
              {competitor.price && competitor.price !== '—' && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-400">Precio</p>
                  <p className="text-sm font-bold text-slate-lighter">{competitor.price}</p>
                </div>
              )}

              {/* Rating y reseñas (Amazon) */}
              {competitor.rating && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 text-slate-lighter fill-slate-lighter" />
                    <span className="text-sm font-bold text-slate-lighter">{competitor.rating}</span>
                  </div>
                  {competitor.reviews > 0 && (
                    <p className="text-xs text-slate-400">{competitor.reviews.toLocaleString()} reseñas</p>
                  )}
                </div>
              )}

              {/* Extracto del anuncio (Facebook) */}
              {isFb && competitor.body && (
                <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed italic">
                  "{competitor.body}…"
                </p>
              )}

              {/* Link */}
              {competitor.url && (
                <a
                  href={competitor.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-slate-light hover:text-slate-lighter transition-colors mt-auto"
                >
                  <ExternalLink className="w-3 h-3" />
                  Ver en {competitor.domain}
                </a>
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-4 p-4 bg-slate-darker rounded-lg border border-slate-light/20">
        <p className="text-sm text-slate-300">
          <span className="font-semibold text-slate-lighter">💡 Análisis de Competencia:</span>{' '}
          {amazonCount > 0 && facebookCount > 0
            ? `${amazonCount} productos líderes en Amazon y ${facebookCount} anunciantes activos en Facebook Ads. Estudia sus precios y mensajes para diferenciarte.`
            : amazonCount > 0
            ? 'Productos con mayor presencia en Amazon. El posicionamiento se calcula con rating y número de reseñas.'
            : `${facebookCount} marcas invirtiendo en Facebook Ads para este término. Analiza sus mensajes y CTAs.`
          }
        </p>
      </div>
    </div>
  )
}
